import { NonOptional } from '@insight-health-ai/roseate/utils';
import { useEffect, useRef, useState } from 'react';
import type H5AudioPlayer from 'react-h5-audio-player';
import ReactAudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import styled from '@emotion/styled';

const StyledAudioPlayer = styled(ReactAudioPlayer)``;

export type AudioPlayerProps = NonOptional<
  Omit<ReactAudioPlayer['props'], 'className'>,
  'src'
> & {
  playbackRate?:
    | {
        enabled?: boolean;
        default: number;
        options: number[];
      }
    | false;
};

const defaultPlaybackRateProps = {
  enabled: true,
  default: 1,
  options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
};

export const AudioPlayer = ({
  src,
  playbackRate: playbackRateProps = defaultPlaybackRateProps,
  ...rest
}: AudioPlayerProps) => {
  const audioRef = useRef<H5AudioPlayer | null>(null);

  const playbackRateConfig =
    typeof playbackRateProps === 'object'
      ? { ...defaultPlaybackRateProps, ...playbackRateProps }
      : { ...defaultPlaybackRateProps, enabled: !!playbackRateProps };

  const [playbackRate, setPlaybackRate] = useState(
    playbackRateConfig?.default ?? 1
  );

  useEffect(() => {
    if (audioRef.current?.audio.current) {
      audioRef.current.audio.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const controls = [
    RHAP_UI.MAIN_CONTROLS,
    ...(playbackRateConfig.enabled
      ? [
          <div
            key="playback-rate"
            className="flex items-center px-2 cursor-pointer text-md"
            onClick={() =>
              setPlaybackRate(
                (prev: number) =>
                  playbackRateConfig.options[
                    (playbackRateConfig.options.indexOf(prev) + 1) %
                      playbackRateConfig.options.length
                  ]
              )
            }
          >
            {playbackRate}x
          </div>,
        ]
      : []),
    RHAP_UI.VOLUME_CONTROLS,
  ];

  return (
    <StyledAudioPlayer
      ref={audioRef}
      customControlsSection={controls}
      layout="horizontal"
      src={src}
      {...rest}
    />
  );
};
